import React, { useCallback, useMemo, useState } from 'react';

import { Modal, Table } from '@components/common';
import FlatTree from '@components/common/FlatTree';
import PageTitle from '@components/common/PageTitle';
import ProfileTabSection from '@components/common/ProfileTabSection';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm/FileTypeSelectionForm';

import { Icons } from '@icons/icons';

import { useMutateNetconfEventsExport } from '@hooks/vesEvents/useMutateNetconfEventsExport';
import { useMutateVesEventsExport } from '@hooks/vesEvents/useMutateVesEventsExport';
import { useQueryNetconfEvents } from '@hooks/vesEvents/useQueryNetconfEvents';
import { useQueryNetconfEventsDropdownValues } from '@hooks/vesEvents/useQueryNetconfEventsDropdownValues';
import { useQueryVesEvents } from '@hooks/vesEvents/useQueryVesEvents';
import { useQueryVesEventsDropdownValues } from '@hooks/vesEvents/useQueryVesEventsDropdownValues';
import { useQueryVesRawDataById } from '@hooks/vesEvents/useQueryVesRawDataById';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { isEmpty } from '@utils/common';
import { convertToFlatTreeData } from '@components/common/FlatTree/utils';
import { getNetconfColumns } from './helpers/getNetconfColumns';
import { getVesColumns } from '@components/routes/AdminPanel/VesEvents/helpers/getVesColumns';

import { IExportDefaultDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { ITabTableState } from '@interfaces/components/tabSection';
import { INetconfEventsDTO } from '@interfaces/vesData/dto/netconfEventsDTO';
import { IVesEventsExportFiltersDTO } from '@interfaces/vesData/dto/vesEventsDTO';

import {
	defaultMeta,
	EAdminPanelPageTitles,
	TMenuActionsTypes,
} from '@enums/common';

import colors from '@constants/colors';
import { exportRadioButtonsData } from '@constants/common';
import { MODAL_WIDTH_480 } from '@constants/modals';
import { DEFAULT_TABS, EVENT_TABS, EVENTS_TABS_NAMES } from './constants';

import { getAlarmFilterValues } from '../AlarmManagement/AlarmManagementByVendor/utils';
import { generateAlarmsFilters } from '../Alarms/AlarmByVendor/utils';
import PreviewNetconfEvent from './PreviewNetconfEvent';
import { getMenuActionWithCount } from './utils';

import styles from './VesEventsTable.module.scss';

const VesEventsTable = () => {
	const { activeOrganisation: organizationId } = useAppSelector(auth);

	const { vesEventsFilters } = useQueryVesEventsDropdownValues();
	const { netconfFilters } = useQueryNetconfEventsDropdownValues();

	const [currentTab, setCurrentTab] = useState(EVENTS_TABS_NAMES.vesEvents);
	const [tableState, setTableState] = useState<ITabTableState>(DEFAULT_TABS);

	const [currentVesDataId, setCurrentVesDataId] = useState<string | undefined>();

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	} = tableState[currentTab];

	const isVesEventsTab = currentTab === EVENTS_TABS_NAMES.vesEvents;

	const clearPagination = useCallback(() => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				page: defaultMeta.page,
				pageSize: defaultMeta.pageSize,
			},
		}));
	}, [setTableState, currentTab]);

	const setFilters = useCallback((value: Record<string, TSelectOption>) => {
		clearPagination();
		setTableState((prevState) => {
			return {
				...prevState,
				[currentTab]: {
					...prevState[currentTab],
					filter: {
						...prevState[currentTab].filter,
						...value,
					},
				},
			};
		},
		);
	}, [currentTab, setTableState, clearPagination]);

	const filterValues = useMemo(() => {
		return getAlarmFilterValues(tableState[currentTab].filter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableState[currentTab].filter]);

	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
	const [exportType, setExportType] = useState<TMenuActionsTypes>();

	const [isVesRawDataModalOpen, setIsVesRawDataModalOpen] = useState(false);
	const [isNetconfPreviewModalOpen, setIsNetconfPreviewModalOpen] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const {
		vesEvents,
		isLoadingVesEvents,
		countOfRecords,
	} = useQueryVesEvents({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		isEnabled: isVesEventsTab,
		...filterValues,
	});

	const {
		netconfEvents,
		isLoadingNetconfEvents,
		countOfRecords: netconfEventsCount,
	} = useQueryNetconfEvents({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		isEnabled: !isVesEventsTab,
		organizationId,
		...filterValues,
	});

	const { vesEventRawData } = useQueryVesRawDataById({ id: isVesEventsTab ? currentVesDataId : undefined });

	const columns = useMemo(
		() => currentTab === EVENTS_TABS_NAMES.vesEvents
			? getVesColumns({
				setCurrentVesDataId,
				setIsVesRawDataModalOpen,
			})
			: getNetconfColumns({
				setCurrentVesDataId,
				setIsNetconfPreviewModalOpen,
			}),
		[currentTab],
	);

	const filters: TTableFilter[] = useMemo(() => {
		return generateAlarmsFilters(
			isVesEventsTab ? vesEventsFilters : netconfFilters,
			tableState[currentTab].filter,
			setFilters,
		);
	}, [
		vesEventsFilters,
		netconfFilters,
		currentTab,
		tableState,
		isVesEventsTab,
		setFilters,
	]);

	const searchHandler = useCallback((searchValue: string) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				searchValue,
				page: defaultMeta.page,
			},
		}));
	}, [setTableState, currentTab]);

	const pageHandler = useCallback((page: number) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				page,
			},
		}));
	}, [currentTab]);

	const pageSizeHandler = useCallback((pageSize: number) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				pageSize,
				page: defaultMeta.page,
			},
		}));
	}, [currentTab]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				sortDirection: sortBy[0]?.desc || false,
				sortFieldName: sortBy[0]?.id,
			},
		}));
	}, [currentTab]);

	const toggleNetconfPreviewModal = useCallback(() => {
		setIsNetconfPreviewModalOpen((prevState) => !prevState);
		setCurrentVesDataId(undefined);
	}, []);

	const toggleRawDataModal = useCallback(() => {
		setIsVesRawDataModalOpen((prevState) => !prevState);
		setCurrentVesDataId(undefined);
	}, []);

	const toggleFileTypeSelectionModal = useCallback(() => {
		setIsOpenFileTypeSelectionModal((prev) => !prev);
	}, []);

	const sort: ITableSort = useMemo(
		() => ({
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}),
		[sortFieldName, sortDirection, handleSort],
	);

	const pagination: ITablePagination = useMemo(() => {
		return {
			countOfRecords: isVesEventsTab ? countOfRecords : netconfEventsCount,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		};
	}, [
		page,
		pageSize,
		countOfRecords,
		netconfEventsCount,
		isVesEventsTab,
		pageHandler,
		pageSizeHandler,
	]);

	const search: ITableSearch[] = useMemo(
		() => [
			{
				initialValue: searchValue,
				name: 'name',
				handler: searchHandler,
				tooltipText: 'Search',
			},
		],
		[searchValue, searchHandler],
	);

	const treeData = useMemo(() => {
		if (!vesEventRawData) return [];

		return convertToFlatTreeData(JSON.parse(vesEventRawData));
	}, [vesEventRawData]);

	const { exportVesEvents, isExportingVesEvents } = useMutateVesEventsExport();
	const { exportNetconfEvents, isExportingNetconfEvents } = useMutateNetconfEventsExport();

	const onExportVesEvents = useCallback(
		(ids: string[], actionType?: TMenuActionsTypes) => {
			setSelectedRowIds(ids);
			setExportType(actionType);
			toggleFileTypeSelectionModal();
		},
		[toggleFileTypeSelectionModal],
	);

	const activeExport = useCallback(({ fileType, ids, filter, isAll }: IExportDefaultDTO<undefined>) => {
		if (isVesEventsTab) {
			exportVesEvents({
				fileType,
				ids,
				filter: filter as IVesEventsExportFiltersDTO,
				isAll,
			});
		} else {
			exportNetconfEvents({
				fileType,
				ids,
				filter: {
					...filter as INetconfEventsDTO,
					sortDirection: filter?.sortDirection ?? false,
					sortFieldName: filter?.sortFieldName ?? null,
					page: filter?.page ?? defaultMeta.page,
					pageSize: filter?.pageSize ?? defaultMeta.pageSize,
				},
				isAll,
			});
		}
	}, [exportVesEvents, exportNetconfEvents, isVesEventsTab]);

	const closeTypeSelectionForm = useCallback(() => setIsOpenFileTypeSelectionModal(false), []);

	const tabCountOfRecords = useMemo(() => {
		return isVesEventsTab ? countOfRecords : netconfEventsCount;
	}, [isVesEventsTab, countOfRecords, netconfEventsCount]);

	const customActions = useMemo(() => {
		return getMenuActionWithCount({
			all: tabCountOfRecords,
			filtered: tabCountOfRecords,
			current: tabCountOfRecords < pageSize ? tabCountOfRecords : pageSize,
			selected: selectedRowIds.length,
		});
	}, [
		tabCountOfRecords,
		selectedRowIds.length,
		pageSize,
	]);

	const getCurrentTab = useCallback(() => {
		switch (currentTab) {
			case EVENTS_TABS_NAMES.vesEvents:
				return (
					<Table
						key={EVENTS_TABS_NAMES.vesEvents}
						columns={columns}
						data={vesEvents}
						filters={filters}
						handleExport={onExportVesEvents}
						isExport
						isLoading={isLoadingVesEvents}
						isOpenSearch={!!searchValue}
						isWithoutDeleteAction
						pagination={pagination}
						search={search}
						sort={sort}
					/>
				);
			case EVENTS_TABS_NAMES.netconfEvents:
				return (
					<Table
						key={EVENTS_TABS_NAMES.netconfEvents}
						columns={columns}
						customExportActions={customActions}
						data={netconfEvents}
						filters={filters}
						handleExport={onExportVesEvents}
						isExport
						isLoading={isLoadingNetconfEvents}
						isOpenSearch={!!searchValue}
						isWithoutDeleteAction
						pagination={pagination}
						setSelectedIds={setSelectedRowIds}
						sort={sort}
					/>
				);
			default:
				return null;
		}
	}, [
		currentTab,
		columns,
		filters,
		pagination,
		search,
		sort,
		isLoadingVesEvents,
		isLoadingNetconfEvents,
		netconfEvents,
		vesEvents,
		searchValue,
		onExportVesEvents,
		customActions,
	]);

	return (
		<>
			<div className={styles.wrapper}>
				<PageTitle
					icon={Icons.middlewareSMO({ isActive: true, color: colors.white })}
					title={EAdminPanelPageTitles.VESEvents}
				/>
				<div className={styles.tableContainer}>
					<ProfileTabSection
						currentTab={currentTab}
						getCurrentTab={getCurrentTab}
						isFullHeight
						isWithoutShadow
						maxWidth={'unset'}
						setCurrentTab={setCurrentTab}
						tabs={EVENT_TABS}
					/>
				</div>
			</div>
			{currentVesDataId && (
				<Modal
					height='auto'
					isOpen={isNetconfPreviewModalOpen}
					onClose={toggleNetconfPreviewModal}
					title='Preview event'
					width='840px'
				>
					<PreviewNetconfEvent
						id={currentVesDataId}
						onClose={toggleNetconfPreviewModal}
					/>
				</Modal>
			)}

			{isVesRawDataModalOpen && !isEmpty(treeData) && (
				<Modal
					height='auto'
					isOpen
					onClose={toggleRawDataModal}
					title='View VES event message'
					width='840px'
				>
					<FlatTree
						cancelHandler={toggleRawDataModal}
						isCloseButtonOnly
						isCopyButtonNeeded
						isReadOnly
						rawData={vesEventRawData}
						treeData={treeData}
					/>
				</Modal>
			)}

			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_480}
			>
				<FileTypeSelectionForm
					activeGroupTableAction={exportType}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					filter={{
						...filterValues,
						page,
						pageSize,
						sortDirection,
						sortFieldName,
						searchValue,
					}}
					handlePreparingExport={activeExport}
					isLoading={isExportingVesEvents || isExportingNetconfEvents}
					selectedIds={selectedRowIds}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>

		</>
	);
};

export default VesEventsTable;
